import { adverseKindOfRootFolder, type MovableDocumentCategory, type PartieAdverseSubcategory } from '@hector/shared-types';

/**
 * Modèle de la DESTINATION d'une pièce — fonctions pures, testées sans DOM.
 *
 * Une destination, dans Hector, c'est toujours le même triplet :
 *   (a) le DOSSIER (« case »),
 *   (b) l'ONGLET (`document_category`),
 *   (c) le sous-dossier de l'arborescence (`folder_path`, vide = racine).
 *
 * Il vivait jusqu'ici réécrit dans chaque écran, avec des règles divergentes :
 * `flattenFolders` existait en trois exemplaires, l'onglet s'appelait « Section »,
 * « Onglet » ou « Emplacement » selon la fenêtre, et seuls deux écrans sur quatre
 * laissaient changer de dossier. Ce module est la source unique de ces règles ;
 * le rendu vit dans `DestinationPicker`.
 */

/** Une destination complète, telle qu'un appelant la consomme pour écrire. */
export interface DestinationValue {
  /** `''` tant qu'aucun dossier n'est choisi — l'arbre n'est alors pas chargeable. */
  caseId: string;
  category: MovableDocumentCategory;
  /** Chemin du sous-dossier ; `[]` = racine de l'onglet. */
  folderPath: string[];
}

/** Un nœud d'arborescence, normalisé depuis la réponse de `useFolderTree`. */
export interface DestinationFolderNode {
  id: string;
  name: string;
  path: string[];
  documentCount?: number;
  subcategory?: string;
  children?: DestinationFolderNode[];
}

/**
 * Normalise l'arbre rendu par `folderHierarchyService` en nœuds à chemin ABSOLU.
 *
 * WHY le chemin est recalculé ici plutôt que lu du nœud : la destination écrite en
 * base est un `folder_path` absolu, et un chemin relatif au parent produirait une
 * pièce rangée dans un dossier homonyme d'un autre niveau. C'est la même raison qui
 * a fait supprimer `navigateToFolder(name)` de la navigation (cf. CLAUDE.md
 * « Navigation dossiers — un seul chemin de vérité »).
 */
export function toDestinationTree(raw: unknown, parentPath: string[] = []): DestinationFolderNode[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const node = item as {
      id?: string;
      name?: unknown;
      documentCount?: number;
      fileCount?: number;
      subcategory?: string;
      children?: unknown;
    };
    if (typeof node.name !== 'string' || node.name.length === 0) return [];
    const path = [...parentPath, node.name];
    return [
      {
        id: node.id ?? path.join('/'),
        name: node.name,
        path,
        documentCount: node.documentCount ?? node.fileCount,
        subcategory: node.subcategory,
        children: toDestinationTree(node.children, path),
      },
    ];
  });
}

/** Deux chemins désignent-ils le même dossier ? */
export function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((segment, i) => segment === b[i]);
}

/** `path` est-il `ancestor` lui-même, ou l'un de ses descendants ? */
export function isWithin(path: readonly string[], ancestor: readonly string[]): boolean {
  return ancestor.length <= path.length && ancestor.every((segment, i) => segment === path[i]);
}

/**
 * La racine de l'onglet est-elle une destination valide ?
 *
 * Non sous « Partie adverse » : cet onglet n'a pas de liste racine, il rend deux
 * dossiers typés et filtre les pièces par `document_subcategory`. Une pièce posée
 * à sa racine ne serait navigable NULLE PART — invisible pour l'avocat, alors
 * qu'aucune erreur ne l'aurait signalé.
 */
export function rootIsSelectable(category: MovableDocumentCategory): boolean {
  return category !== 'partie_adverse';
}

/**
 * La destination est-elle complète et écrivable ?
 *
 * Rend la RAISON quand elle ne l'est pas : l'écran doit pouvoir dire pourquoi le
 * bouton est inactif. Un bouton désactivé sans phrase force l'avocat à deviner,
 * et une infobulle ne s'ouvre pas au clavier sur un bouton désactivé.
 */
export function describeDestinationIssue(value: DestinationValue): string | null {
  if (!value.caseId) return 'Choisissez le dossier de destination.';
  if (!rootIsSelectable(value.category) && value.folderPath.length === 0) {
    return 'Choisissez un dossier « Écritures adverses » ou « Pièces adverses ».';
  }
  return null;
}

/**
 * Applique un changement de dossier.
 *
 * L'arborescence est scopée au couple (dossier, onglet) : un sous-dossier choisi
 * dans l'ancien dossier n'existe pas dans le nouveau. Le garder produirait un
 * `folder_path` fantôme — une pièce rangée dans un dossier qui n'existe pas.
 */
export function withCase(value: DestinationValue, caseId: string): DestinationValue {
  if (caseId === value.caseId) return value;
  return { ...value, caseId, folderPath: [] };
}

/** Applique un changement d'onglet — même raison de remise à zéro du chemin. */
export function withCategory(
  value: DestinationValue,
  category: MovableDocumentCategory,
): DestinationValue {
  if (category === value.category) return value;
  return { ...value, category, folderPath: [] };
}

/**
 * Le TAG (`document_subcategory`) que la pièce recevra sous « Partie adverse ».
 *
 * Dans cet onglet, le dossier est le rangement et le tag la sémantique : ce sont
 * les lecteurs backend (RAG, agent, bordereau) qui interrogent le tag, jamais le
 * nom du dossier — lequel est librement renommable. Le tag se dérive donc du
 * dossier RACINE du chemin choisi, par sa propre étiquette d'abord, par son nom
 * seulement en repli (paires legacy jamais taguées).
 *
 * Rend `undefined` hors « Partie adverse », à la racine, ou quand le dossier
 * racine n'est pas typable — trois cas où l'écran doit refuser d'écrire plutôt
 * que d'inventer un tag : une pièce mal taguée disparaît de l'onglet.
 */
export function partieAdverseTargetKind(
  value: DestinationValue,
  rootFolders: readonly Pick<DestinationFolderNode, 'name' | 'subcategory'>[],
): PartieAdverseSubcategory | undefined {
  if (value.category !== 'partie_adverse' || value.folderPath.length === 0) return undefined;
  const rootName = value.folderPath[0];
  if (!rootName) return undefined;
  const root = rootFolders.find((node) => node.name === rootName);
  return adverseKindOfRootFolder({ name: rootName, subcategory: root?.subcategory });
}
